/**
 * Aura icon (buff / debuff): painterly icon or initials, duration sweep, time left, stacks,
 * buff / debuff / dispel-type border, and the shared tooltip (describeAura via auraTooltipModel).
 *
 * AuraRow manages a pooled row of icons for one unit and is safe to call every frame: it walks
 * `unit.auras` in place (no filter/slice), reuses icons, writes only what changed, and the
 * tooltip reads a mutable live context instead of a per-frame closure.
 */
import type { AuraDef, AuraInstance, ContentRegistry, DeepReadonly, Unit, UnitId, World } from '@keystone/sim';
import { SCHOOL_COLOR } from '../../render/palette';
import { el } from '../dom';
import { iconUrl } from '../icons';
import { initials } from '../format';
import { type TooltipLive, auraTooltipModel } from '../tooltip/hud';
import { attachAbilityTooltip } from '../tooltip/view';
import { auraTimeKey, timerLabel } from './slotState';
import { DEG, intText, sweepIndex } from './tokens';

export type AuraIconSize = 'xs' | 'sm';

/** Below this much time left the icon blinks (opacity-only animation). */
export const AURA_EXPIRING_MS = 3000;

export class AuraIcon {
  readonly root: HTMLElement;
  private readonly img: HTMLImageElement;
  private readonly fallback: HTMLElement;
  private readonly sweep: HTMLElement;
  private readonly time: HTMLElement;
  private readonly stacks: HTMLElement;
  /** Instance shown (tooltip key). */
  iid: string | null = null;
  private auraId: string | null = null;
  private kindCls = '';
  private sweepIdx = -1;
  private timeKey = -1;
  private stacksShown = -1;
  private expiring = false;
  private shown = true;

  /** `tooltip`: provider for the hover tooltip (AuraRow wires auraTooltipModel). */
  constructor(parent: HTMLElement | null, size: AuraIconSize = 'xs', tooltip?: () => ReturnType<typeof auraTooltipModel>) {
    this.root = el('div', `hk-aura hk-hit ${size}`);
    this.img = el('img', 'hk-aura-img', this.root);
    this.img.alt = '';
    this.img.draggable = false;
    this.img.decoding = 'async';
    this.fallback = el('div', 'hk-aura-fallback', this.root);
    this.sweep = el('div', 'hk-aura-sweep', this.root);
    this.time = el('div', 'hk-aura-time hk-num', this.root);
    this.stacks = el('div', 'hk-aura-stacks hk-num', this.root);
    if (tooltip) attachAbilityTooltip(this.root, tooltip);
    parent?.appendChild(this.root);
  }

  /** Draw an aura instance at sim time `now` (hudNow for smooth sweeps). */
  set(inst: DeepReadonly<AuraInstance>, def: DeepReadonly<AuraDef> | undefined, now: number, content?: ContentRegistry): void {
    const iid = inst.iid as unknown as string;
    this.iid = iid;
    if (inst.auraId !== this.auraId) {
      this.auraId = inst.auraId;
      const url = iconUrl(inst.auraId, 64) ?? iconUrl(inst.abilityId, 64);
      if (url) {
        this.img.src = url;
        this.img.hidden = false;
        this.fallback.hidden = true;
      } else {
        this.img.removeAttribute('src');
        this.img.hidden = true;
        this.fallback.hidden = false;
        this.fallback.textContent = initials(def?.name ?? inst.auraId);
        const ab = content?.ability(inst.abilityId);
        this.fallback.style.setProperty('--school', ab?.ui?.color ?? SCHOOL_COLOR[ab?.school ?? 'physical'] ?? '#888');
      }
      const cls = def?.kind === 'debuff' ? `debuff${def.dispelType ? ` dispel-${def.dispelType}` : ''}` : 'buff';
      if (cls !== this.kindCls) {
        if (this.kindCls) this.root.classList.remove(...this.kindCls.split(' '));
        this.root.classList.add(...cls.split(' '));
        this.kindCls = cls;
      }
    }
    const left = inst.expiresAt === null ? null : Math.max(0, inst.expiresAt - now);
    const dur = inst.durationMs > 0 ? inst.durationMs : 1;
    const si = left === null ? 0 : sweepIndex(1 - left / dur);
    if (si !== this.sweepIdx) {
      this.sweepIdx = si;
      this.sweep.style.setProperty('--p', DEG[si]!);
    }
    const tk = auraTimeKey(left);
    if (tk !== this.timeKey) {
      this.timeKey = tk;
      this.time.textContent = timerLabel(tk);
    }
    const st = inst.stacks > 1 ? inst.stacks : 0;
    if (st !== this.stacksShown) {
      this.stacksShown = st;
      this.stacks.textContent = st ? intText(st) : '';
    }
    const exp = left !== null && left < AURA_EXPIRING_MS;
    if (exp !== this.expiring) {
      this.expiring = exp;
      this.root.classList.toggle('is-expiring', exp);
    }
    this.show(true);
  }

  show(on: boolean): void {
    if (on === this.shown) return;
    this.shown = on;
    this.root.classList.toggle('hk-hidden', !on);
    if (!on) this.iid = null;
  }
}

export interface AuraRowOptions {
  max?: number;
  size?: AuraIconSize;
  /** Which auras to show (default: everything not flagged hidden). Called per aura per frame: keep it cheap. */
  filter?: (inst: DeepReadonly<AuraInstance>, def: DeepReadonly<AuraDef> | undefined, playerId: UnitId | null) => boolean;
  className?: string;
}

const defaultFilter: NonNullable<AuraRowOptions['filter']> = (_i, def) => !def?.flags?.hidden;

/** Pooled aura icons for one unit. */
export class AuraRow {
  readonly root: HTMLElement;
  private readonly icons: AuraIcon[] = [];
  private readonly max: number;
  private readonly size: AuraIconSize;
  private readonly filter: NonNullable<AuraRowOptions['filter']>;
  /** Mutable tooltip context (updated in place each frame). */
  private readonly live: TooltipLive & { unitId: UnitId | null } = { world: null as never, content: null as never, playerId: null, unitId: null };

  constructor(parent: HTMLElement | null, opts: AuraRowOptions = {}) {
    this.root = el('div', `hk-auras${opts.className ? ` ${opts.className}` : ''}`);
    this.max = opts.max ?? 8;
    this.size = opts.size ?? 'xs';
    this.filter = opts.filter ?? defaultFilter;
    parent?.appendChild(this.root);
  }

  /** `now`: sim-time present (hudNow). `unit` undefined clears the row. */
  update(world: DeepReadonly<World>, content: ContentRegistry, unit: DeepReadonly<Unit> | undefined, playerId: UnitId | null, now: number): void {
    this.live.world = world;
    this.live.content = content;
    this.live.playerId = playerId;
    this.live.unitId = unit?.id ?? null;
    let n = 0;
    if (unit) {
      for (const inst of unit.auras) {
        if (n >= this.max) break;
        const def = content.aura(inst.auraId);
        if (!this.filter(inst, def, playerId)) continue;
        this.icon(n).set(inst, def, now, content);
        n++;
      }
    }
    for (let i = n; i < this.icons.length; i++) this.icons[i]!.show(false);
  }

  private icon(i: number): AuraIcon {
    let ic = this.icons[i];
    if (!ic) {
      const box: { icon: AuraIcon | null } = { icon: null };
      ic = new AuraIcon(this.root, this.size, () => auraTooltipModel(this.live.world ? this.live : null, this.live.unitId, box.icon?.iid));
      box.icon = ic;
      this.icons.push(ic);
    }
    return ic;
  }
}
